import React from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, Filler
} from "chart.js";
import { STATUS_COLORS, STATUS_LABELS, fmtMoney } from "./ui";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, Filler);

const FONT = { family: "Open Sans, Segoe UI, Arial, sans-serif", size: 12 };
const GRID = "#EEF0F5";
const INK = "#6B7280";

/** Horizontal stacked bars: one row per company, segments by status. */
export function CompanyStackedBar({ companies, onClick }) {
  const rows = (companies || []).slice(0, 10);
  const keys = ["completed", "scheduled", "candidateNoShow", "panelNoShow", "cancelled", "rescheduled", "glitches"];
  const data = {
    labels: rows.map((c) => c.companyName),
    datasets: keys.map((k) => ({
      label: STATUS_LABELS[k],
      data: rows.map((c) => c.statusBreakdown?.[k] || 0),
      backgroundColor: STATUS_COLORS[k],
      borderWidth: 0,
      barThickness: 16,
      borderSkipped: false
    }))
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    onClick: (evt, el) => { if (el?.length && onClick) onClick(rows[el[0].index]); },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          footer: (items) => { const c = rows[items[0].dataIndex]; return `Total ${c.interviews} · ${c.changePct >= 0 ? "▲" : "▼"} ${Math.abs(c.changePct)}% vs prior`; }
        }
      }
    },
    scales: {
      x: { stacked: true, grid: { color: GRID }, ticks: { font: FONT, color: INK }, border: { display: false } },
      y: { stacked: true, grid: { display: false }, ticks: { font: FONT, color: "#3A3A3A" }, border: { display: false } }
    }
  };
  return <div style={{ height: Math.max(200, rows.length * 38 + 40) }}><Bar data={data} options={options} /></div>;
}

/** Monthly interviews line: total + completed. */
export function TrendLine({ trend, showCompleted = true }) {
  const t = trend || [];
  const data = {
    labels: t.map((m) => m.label),
    datasets: [
      { label: "All interviews", data: t.map((m) => m.total), borderColor: "#3E67B1", backgroundColor: "#3E67B1", borderWidth: 2, pointRadius: 4, pointBackgroundColor: "#3E67B1", pointBorderColor: "#FFFFFF", pointBorderWidth: 2, tension: 0.25 },
      ...(showCompleted ? [{ label: "Completed", data: t.map((m) => m.completed), borderColor: "#2E8B57", backgroundColor: "#2E8B57", borderWidth: 2, pointRadius: 4, pointBackgroundColor: "#2E8B57", pointBorderColor: "#FFFFFF", pointBorderWidth: 2, tension: 0.25 }] : [])
    ]
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: "top", align: "end", labels: { font: FONT, color: INK, boxWidth: 10, boxHeight: 10, usePointStyle: true } } },
    scales: {
      x: { grid: { display: false }, ticks: { font: FONT, color: INK }, border: { display: false } },
      y: { beginAtZero: true, grid: { color: GRID }, ticks: { font: FONT, color: INK, precision: 0 }, border: { display: false } }
    }
  };
  return <div style={{ height: 240 }}><Line data={data} options={options} /></div>;
}

/** Monthly revenue vs panel cost bars (INR). */
export function MoneyTrend({ trend, currency = "INR" }) {
  const t = trend || [];
  const data = {
    labels: t.map((m) => m.label),
    datasets: [
      { label: "Revenue billed", data: t.map((m) => m.revenue?.[currency] || 0), backgroundColor: "#3E67B1", borderRadius: 4, barThickness: 18 },
      { label: "Panel cost", data: t.map((m) => m.panelCost?.[currency] || 0), backgroundColor: "#9BB2DD", borderRadius: 4, barThickness: 18 }
    ]
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", align: "end", labels: { font: FONT, color: INK, boxWidth: 10, boxHeight: 10 } },
      tooltip: { callbacks: { label: (i) => `${i.dataset.label}: ${fmtMoney(i.raw, currency, false)}` } }
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: FONT, color: INK }, border: { display: false } },
      y: { beginAtZero: true, grid: { color: GRID }, ticks: { font: FONT, color: INK, callback: (v) => fmtMoney(v, currency) }, border: { display: false } }
    }
  };
  return <div style={{ height: 240 }}><Bar data={data} options={options} /></div>;
}

/** Workload per admin with a team-average reference. */
export function WorkloadBar({ scorecard, average }) {
  const rows = [...(scorecard || [])].sort((a, b) => b.interviews - a.interviews);
  const data = {
    labels: rows.map((m) => m.name),
    datasets: [{
      label: "Interviews coordinated",
      data: rows.map((m) => m.interviews),
      backgroundColor: rows.map((m) => (average && m.interviews > average * 1.5 ? "#D9822B" : "#3E67B1")),
      borderRadius: 4, barThickness: 16
    }]
  };
  const options = {
    indexAxis: "y", responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { footer: () => (average ? `Team average ${Math.round(average)}` : "") } } },
    scales: {
      x: { beginAtZero: true, grid: { color: GRID }, ticks: { font: FONT, color: INK, precision: 0 }, border: { display: false } },
      y: { grid: { display: false }, ticks: { font: FONT, color: "#3A3A3A" }, border: { display: false } }
    }
  };
  return <div style={{ height: Math.max(180, rows.length * 38 + 30) }}><Bar data={data} options={options} /></div>;
}

/** Team score trend with a target line. */
export function ScoreTrend({ trend, target = 75 }) {
  const t = trend || [];
  const data = {
    labels: t.map((m) => m.label),
    datasets: [
      { label: "Average score", data: t.map((m) => m.avgScore), borderColor: "#3E67B1", backgroundColor: "#3E67B1", borderWidth: 2, pointRadius: 4, pointBackgroundColor: "#3E67B1", pointBorderColor: "#FFFFFF", pointBorderWidth: 2, tension: 0.25 },
      { label: `Target ${target}`, data: t.map(() => target), borderColor: "#2E8B57", borderDash: [5, 5], borderWidth: 1.5, pointRadius: 0 }
    ]
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: "top", align: "end", labels: { font: FONT, color: INK, boxWidth: 10, boxHeight: 10, usePointStyle: true } } },
    scales: {
      x: { grid: { display: false }, ticks: { font: FONT, color: INK }, border: { display: false } },
      y: { min: 0, max: 100, grid: { color: GRID }, ticks: { font: FONT, color: INK }, border: { display: false } }
    }
  };
  return <div style={{ height: 240 }}><Line data={data} options={options} /></div>;
}
